import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react'
import { getInboxEmailMeta } from '@/services/api'
import type { InboxEmailMeta } from '@/types/inbox'
import { getDeploymentStage } from '@/constants/deployment'
import { isFrontFlowOrg } from '@/constants/frontflowOrg'
import { useAuth } from '@/contexts/AuthContext'
import {
  buildCloudWatchProcessLogsUrl,
  canLinkProcessIdToCloudWatch,
  type WorkflowProduct,
} from '@/utils/cloudWatchLogs'

/**
 * Email and process identifiers, loaded only when opened.
 *
 * Both inboxes use this — it carries nothing specific to either product. The sales order inbox
 * passes its own fetcher, query key and product so its reads stay under the sales order prefix
 * and its process log link points at the sales order blocks; the order confirmation inbox gets
 * the defaults.
 */
export function EmailMetaPanel({
  workflowId,
  emailId,
  fetchMeta = getInboxEmailMeta,
  queryKeyPrefix = 'inbox-email-meta',
  product = 'order-confirmation',
}: {
  workflowId: string
  emailId: string
  fetchMeta?: (workflowId: string, emailId: string) => Promise<InboxEmailMeta>
  queryKeyPrefix?: string
  product?: WorkflowProduct
}) {
  const { user } = useAuth()
  const [expanded, setExpanded] = useState(false)
  const deploymentStage = getDeploymentStage()

  const { data, isLoading, isError } = useQuery({
    queryKey: [queryKeyPrefix, workflowId, emailId],
    queryFn: () => fetchMeta(workflowId, emailId),
    enabled: expanded,
    staleTime: 60_000,
  })

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between py-1 text-left"
      >
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          Email details
        </p>
        {expanded
          ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
          : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
      </button>
      {expanded && (
        <div className="mt-1.5 space-y-1 text-xs text-gray-500">
          {isLoading && (
            <div className="flex items-center gap-1.5 text-gray-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading…
            </div>
          )}
          {isError && <p className="text-red-600">Could not load email details.</p>}
          {data && (
            <>
              <p>
                <span className="text-gray-400">Email ID</span>
                <span className="mx-1.5 text-gray-300">·</span>
                <span className="font-mono text-gray-700">{data.email_id}</span>
              </p>
              <p>
                <span className="text-gray-400">Process ID</span>
                <span className="mx-1.5 text-gray-300">·</span>
                {canLinkProcessIdToCloudWatch(
                  data.process_id,
                  deploymentStage,
                  isFrontFlowOrg(user?.tenantId),
                ) && deploymentStage ? (
                  <a
                    href={buildCloudWatchProcessLogsUrl(data.process_id, deploymentStage, product)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-indigo-600 hover:text-indigo-800 hover:underline"
                    title="Search CloudWatch logs for this process (last 24 hours from now)"
                  >
                    {data.process_id}
                  </a>
                ) : (
                  <span className="font-mono text-gray-700">{data.process_id || '—'}</span>
                )}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  )
}
